import { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import Button from "../common/Button";
import Overlay from "../Overlay";
import { ReactComponent as CloseSvg } from "../../assets/auth/login-close.svg";

// You need to replace this with your actual Google Maps API key
const GOOGLE_MAPS_API_KEY = import.meta.env.GOOGLE_MAPS_API_KEY;

const center = {
  lat: 13.7563,
  lng: 100.5018,
};

export default function LocationSharer({ onClose, onShare }) {
  const [userLocation, setUserLocation] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [address, setAddress] = useState(undefined);
  const mapRef = useRef(null);
  const geocoder = useRef(null);
  const searchBoxRef = useRef(null);
  const [mapContainerStyle, setMapContainerStyle] = useState({
    width: "100%",
    height: "100%",
  });

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const ref = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const _height = entries[0].contentRect.height;

        // setMapContainerStyle(curr => ({...curr}));

        // setHeight(_width);

        // if (_height <= 270) {
        //   setSize("xs");
        //   return;
        // }

        // if (_height <= 350) {
        //   setSize("s");
        //   return;
        // }

        // setSize("m");
      });
      resizeObserver.observe(node);
    }
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    geocoder.current = new window.google.maps.Geocoder();

    // Add click listener to the map
    // map.addListener("click", handleMapClick);
  }, []);

  const handleSetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMarkerPosition({ lat: latitude, lng: longitude });
          getAddressFromLatLng({ lat: latitude, lng: longitude });
        },
        (error) => {
          if (error.code == error.PERMISSION_DENIED) {
            alert(
              "You have denied access to your location, please allow in your browser to use current location feature"
            );
            return;
          }
          console.error("Error getting location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const handleShare = () => {
    // const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${markerPosition.lat},${markerPosition.lng}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${markerPosition.lat},${markerPosition.lng}&key=${GOOGLE_MAPS_API_KEY}`;
    // setStaticMapUrl(staticMapUrl);

    const { lat, lng } = markerPosition;

    onShare({
      lat,
      lng,
      address,
    });

    onClose();
  };

  const handleMapClick = (event) => {
    if (event.placeId) {
      // If it's a POI, we need to get its details
      const service = new window.google.maps.places.PlacesService(
        mapRef.current
      );
      service.getDetails({ placeId: event.placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setMarkerPosition({ lat, lng });
          setAddress({ address: place.formatted_address, name: place.name });
        } else {
          console.error("Error fetching place details:", status);
          toast({
            title: "Error",
            description:
              "Unable to fetch details for this location. Please try again.",
            variant: "destructive",
          });
        }
      });

      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarkerPosition({ lat, lng });
    getAddressFromLatLng({ lat, lng });
  };

  const getAddressFromLatLng = (location) => {
    if (geocoder.current) {
      geocoder.current.geocode({ location: location }, (results, status) => {
        if (status === "OK") {
          if (results[0]) {
            setAddress({
              name: results[0].name,
              address: results[0].formatted_address,
            });
          } else {
            console.log("No results found");
          }
        } else {
          console.log("Geocoder failed due to: " + status);
        }
      });
    }
  };

  const onSearchBoxLoad = (ref) => {
    searchBoxRef.current = ref;
  };

  const handlePlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      const newPosition = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setMarkerPosition(newPosition);
      setAddress({
        name: place.name,
        address: place.formatted_address,
      });

      if (mapRef.current) {
        mapRef.current.panTo(newPosition);
        mapRef.current.setZoom(15);
      }
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="location-share">
        <div className="location-share-close" onClick={onClose}>
          <CloseSvg />
          <span className="location-share-close__text">Close</span>
        </div>
        <div className="location-share-content">
          {isLoaded && (
            <StandaloneSearchBox
              onLoad={onSearchBoxLoad}
              onPlacesChanged={handlePlacesChanged}
            >
              <input
                className="location-share-content__searchbox"
                type="text"
                placeholder="Search a place"
              />
            </StandaloneSearchBox>
          )}
          <div className="location-share-content-map" ref={ref}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "100%",
                }}
                center={userLocation ?? center}
                onClick={handleMapClick}
                onLoad={onMapLoad}
                zoom={10}
                streetView={false}
              >
                {markerPosition && <Marker position={markerPosition}></Marker>}
              </GoogleMap>
            ) : null}
            {/* {<img src={staticMapUrl} />} */}
          </div>
          <div className="location-share-content-info">
            {address && (
              <span className="location-share-content-info__address">
                <label style={{ fontWeight: 500, fontSize: 16 }}>
                  Location
                </label>
                <br />
                {address.name && (
                  <>
                    <span style={{ fontWeight: 500 }}>{address.name}</span>
                    <br />
                  </>
                )}
                {address.address}
              </span>
            )}
            <div className="location-share-content-info-ctas">
              <Button
                onClick={handleSetCurrentLocation}
                label="Use current location"
                variant={"secondary"}
              />
              <Button
                onClick={handleShare}
                label="Share Location"
                disabled={!markerPosition}
              />
            </div>
            <div className="location-share-content-info__cancel">
              <Button label={"Cancel"} variant="tertiary" onClick={onClose} />
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
}
